import { CircleInfoIcon, CopyIcon, LinkIcon } from "@xola/icons";
import { flash, Modal, Tooltip } from "@xola/ui-kit";
import React, { useRef } from "react";
import { Event } from "types";
import { SecondaryButton } from "../Common/SecondaryButton";

interface VirtualMeetingUrlsModalProps {
    isOpen: boolean;
    onClose: () => void;
    event?: Event;
}

interface UrlProps {
    sellerUrl?: string;
    travelerUrl?: string;
}

export const VirtualMeetingUrlsModal = ({ isOpen, onClose, event }: VirtualMeetingUrlsModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <Modal.Header>Manage Meeting Links</Modal.Header>
            <ModalContent event={event} onClose={onClose} />
        </Modal>
    );
};

const handleCopyToClipboard = (value: string, event?: Event) => {
    let url = "";
    if (value === "travelerUrl" && event?.virtualMeeting?.travelerUrl) {
        url = event?.virtualMeeting?.travelerUrl;
    }

    if (value === "sellerUrl" && event?.virtualMeeting?.sellerUrl) {
        url = event?.virtualMeeting?.sellerUrl;
    }

    navigator.clipboard.writeText(url);
    flash.success("URL copied to clipboard");
};

const getInitialValues = (event?: Event): UrlProps => {
    if (event) {
        return {
            sellerUrl: event?.virtualMeeting?.sellerUrl,
            travelerUrl: event?.virtualMeeting?.travelerUrl,
        };
    }

    return {
        sellerUrl: "",
        travelerUrl: "",
    };
};

const ModalContent = ({ onClose, event }: { onClose: () => void; event?: Event }) => {
    const values = getInitialValues(event);
    return (
        <>
            <Modal.Body>
                <div className="flex flex-col space-y-4">
                    <UrlComponent
                        event={event}
                        value={values.travelerUrl}
                        urlType="travelerUrl"
                        label="Join URL"
                        tooltipContent="This link is sent to your guests so that they may join this online experience."
                    />
                    <UrlComponent
                        event={event}
                        value={values.sellerUrl}
                        urlType="sellerUrl"
                        label="Start URL"
                        tooltipContent="This link is sent to your guides so that they may start this online experience. If you do not provide this link, the Join URL will be sent instead."
                    />
                </div>
            </Modal.Body>

            <Modal.Footer>
                <SecondaryButton onClick={onClose}>Close</SecondaryButton>
            </Modal.Footer>
        </>
    );
};

const UrlComponent = ({
    value,
    event,
    urlType,
    label,
    tooltipContent,
}: {
    value?: string;
    event?: Event;
    urlType: string;
    label: string;
    tooltipContent: string;
}) => {
    const inputRef = useRef<HTMLInputElement>(null);

    return (
        <div className="flex flex-col space-y-1">
            <div className="space-x-1 text-sm font-bold">
                <span>{label}</span>
                <Tooltip content={tooltipContent} placement="right">
                    <CircleInfoIcon />
                </Tooltip>
            </div>

            <div className="flex flex-row items-center justify-between rounded-lg border border-gray-light hover:border-gray-lighter">
                <LinkIcon size="large" className="ml-3" />
                <input
                    ref={inputRef}
                    readOnly
                    className="ml-1 block w-full border-none p-2.5 text-gray-darker	outline-gray-lighter hover:bg-gray-lighter"
                    value={value}
                />
                <SecondaryButton
                    size="small"
                    className="mx-1"
                    icon={<CopyIcon />}
                    onClick={() => {
                        if (inputRef.current) {
                            inputRef.current?.select();
                        }

                        handleCopyToClipboard(urlType, event);
                    }}
                >
                    <p>Copy</p>
                </SecondaryButton>
            </div>
        </div>
    );
};
